import { useState } from "react";
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Button } from "../../../components/ui/button";

const Chip = ({ item, overlay = false }) => (
  <span className={`inline-flex items-center gap-1.5 h-9 pl-1.5 pr-3 rounded-xl border bg-white text-sm font-medium text-[#12234A] select-none ${overlay ? "shadow-xl border-[#F5551E] rotate-2" : "border-slate-200"}`}>
    <GripVertical size={14} className="text-slate-400" />{item.icon && <item.icon size={15} className="text-[#F5551E]" />}{item.label}
  </span>
);

const SortableChip = ({ id, item }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <span ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} {...attributes} {...listeners}
      className={`cursor-grab active:cursor-grabbing touch-none ${isDragging ? "opacity-30" : ""}`} data-testid={`arrange-item-${id}`}>
      <Chip item={item} />
    </span>
  );
};

const Row = ({ id, title, ids, items }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{title}</p>
      <SortableContext id={id} items={ids} strategy={rectSortingStrategy}>
        <div ref={setNodeRef} data-testid={`arrange-row-${id}`}
          className={`min-h-[56px] flex flex-wrap gap-2 p-2.5 rounded-xl border-2 border-dashed transition-colors ${isOver ? "border-[#F5551E] bg-orange-50/50" : "border-slate-200 bg-slate-50/60"}`}>
          {ids.map((i) => items[i] && <SortableChip key={i} id={i} item={items[i]} />)}
          {!ids.length && <span className="text-xs text-slate-400 self-center px-2">Drop buttons here</span>}
        </div>
      </SortableContext>
    </div>
  );
};

// Drag-and-drop editor for the two admin dashboard button rows.
export const MenuArranger = ({ layout, items, onSave, onCancel, onReset, saving }) => {
  const [rows, setRows] = useState({ top: [...layout.top], tabs: [...layout.tabs] });
  const [active, setActive] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const rowOf = (id) => (rows.top.includes(id) ? "top" : rows.tabs.includes(id) ? "tabs" : id in rows ? id : null);
  const onDragOver = ({ active: a, over }) => {
    if (!over) return;
    const from = rowOf(a.id);
    const to = rowOf(over.id);
    if (!from || !to || from === to) return;
    setRows((r) => {
      const target = r[to];
      const idx = over.id in r ? target.length : target.indexOf(over.id);
      return { ...r, [from]: r[from].filter((x) => x !== a.id), [to]: [...target.slice(0, idx), a.id, ...target.slice(idx)] };
    });
  };
  const onDragEnd = ({ active: a, over }) => {
    setActive(null);
    if (!over) return;
    const row = rowOf(a.id);
    if (row && row === rowOf(over.id) && a.id !== over.id && !(over.id in rows)) {
      setRows((r) => ({ ...r, [row]: arrayMove(r[row], r[row].indexOf(a.id), r[row].indexOf(over.id)) }));
    }
  };
  return (
    <div className="rounded-2xl border border-[#F5551E]/40 bg-white p-4 mb-5 space-y-4" data-testid="menu-arranger">
      <p className="text-sm text-slate-600">Drag the buttons into any order, or between the two rows. This layout is shared by all admins.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setActive(e.active.id)} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
        <Row id="top" title="Top row (buttons)" ids={rows.top} items={items} />
        <Row id="tabs" title="Tab row" ids={rows.tabs} items={items} />
        <DragOverlay>{active && items[active] ? <Chip item={items[active]} overlay /> : null}</DragOverlay>
      </DndContext>
      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="ghost" onClick={onReset} disabled={saving} data-testid="arrange-reset">Reset to default</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="arrange-cancel">Cancel</Button>
          <Button onClick={() => onSave(rows)} disabled={saving} data-testid="arrange-save" className="bg-[#F5551E] hover:bg-[#e04a15]">{saving ? "Saving…" : "Save layout"}</Button>
        </div>
      </div>
    </div>
  );
};
